// src/componentes/arquivos/Arquivos.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { getDatabase, ref, get, query } from 'firebase/database';
import firebaseApp from '../adm/admin/firebaseConfig'; // ajuste o caminho se necessário
import TitleSection from '../utils/TittleTheme'; // ajuste o caminho
import DataTable from '../utils/Tabela';        // ajuste o caminho

// Lista de tipos válidos (exatamente a mesma do admin)
const VALID_TYPES = [
  "farmacia", "aldir_blanc", "controleinterno", "concursopublico", "processoseletivo",
  "quadro", "emendas_parlamentares", "portarias", "editais", "semanario",
  "leismunicipais", "leipaulogustavo", "leiorganica", "diariooficial", "contratos",
  "decretos", "loa", "ldo", "rgf", "ppa", "rreo", "pca", "codigotributario",
  "receitaOrcamentaria", "receitaExtraorcamentaria", "despesaOrcamentaria",
  "despesaExtraorcamentaria", "documentoPagamento", "instrumentosPlanejamento",
  "subvencaoSocial", "emendasImpositivas", "pessoal", "licitacoes", "publicacoes",
  "contratosLicitatorios", "conveniosFederal", "recursosFederais", "estagiarios",
  "covid19", "frotaMunicipal", "regulamentacaoLAI", "regulamentacaoDiarias",
  "renunciasFiscais", "lgpd", "tabelaDiarias"
];

// Mapa de títulos amigáveis (completo)
const TITLE_MAP = {
  farmacia: "Farmácia Básica",
  aldir_blanc: "Aldir Blanc",
  controleinterno: "Controle Interno",
  concursopublico: "Concurso Público",
  processoseletivo: "Processo Seletivo",
  quadro: "Quadro Funcional Mensal",
  emendas_parlamentares: "Emendas Parlamentares",
  portarias: "Portarias",
  editais: "Editais",
  semanario: "Semanário Oficial",
  leismunicipais: "Leis Municipais",
  leipaulogustavo: "Lei Paulo Gustavo",
  leiorganica: "Lei Orgânica Municipal",
  diariooficial: "Diário Oficial",
  contratos: "Contratos",
  decretos: "Decretos",
  loa: "LOA – Lei Orçamentária Anual",
  ldo: "LDO – Lei de Diretrizes Orçamentárias",
  rgf: "RGF – Relatório de Gestão Fiscal",
  ppa: "PPA – Plano Plurianual",
  rreo: "RREO – Relatório Resumido de Execução Orçamentária",
  pca: "PCA – Prestação de Contas Anual",
  codigotributario: "Código Tributário Municipal",
  receitaOrcamentaria: "Receita Orçamentária",
  receitaExtraorcamentaria: "Receita Extraorçamentária",
  despesaOrcamentaria: "Despesa Orçamentária",
  despesaExtraorcamentaria: "Despesa Extraorçamentária",
  documentoPagamento: "Documentos de Pagamento / Empenhos",
  instrumentosPlanejamento: "Instrumentos de Planejamento",
  subvencaoSocial: "Subvenção Social",
  emendasImpositivas: "Emendas Impositivas",
  pessoal: "Pessoal – Folha de Pagamento / Remuneração",
  licitacoes: "Licitações",
  publicacoes: "Publicações Legais",
  contratosLicitatorios: "Contratos Licitatórios",
  conveniosFederal: "Convênios com Entes Federais",
  recursosFederais: "Recursos Federais Recebidos",
  estagiarios: "Estagiários",
  covid19: "COVID-19 – Execução de Recursos",
  frotaMunicipal: "Frota Municipal",
  regulamentacaoLAI: "Regulamentação da Lei de Acesso à Informação (LAI)",
  regulamentacaoDiarias: "Regulamentação de Diárias",
  renunciasFiscais: "Renúncias Fiscais",
  lgpd: "LGPD – Lei Geral de Proteção de Dados",
  tabelaDiarias: "Tabela de Diárias",
};

// Mapa de subtítulos (pode ser expandido)
const SUBTITLE_MAP = {
  leismunicipais: "Normas legais aprovadas pela Câmara Municipal que regulam direitos, deveres e serviços no âmbito do município.",
  diariooficial: "Publicações oficiais do Poder Executivo Municipal.",
  editais: "Editais de licitação, chamamento público, concursos e processos seletivos.",
  contratos: "Contratos firmados pelo município, incluindo aditivos e extratos.",
  decretos: "Decretos municipais expedidos pelo Poder Executivo.",
  loa: "Lei Orçamentária Anual – detalhamento da receita e despesa previstas para o exercício.",
  ldo: "Lei de Diretrizes Orçamentárias – metas e prioridades para o orçamento.",
  rgf: "Relatório de Gestão Fiscal – demonstração do cumprimento dos limites de despesa.",
  ppa: "Plano Plurianual – planejamento de médio prazo do município.",
  rreo: "Relatório Resumido de Execução Orçamentária – acompanhamento bimestral.",
  pca: "Prestação de Contas Anual – resumo das contas do exercício.",
  codigotributario: "Código Tributário Municipal – normas sobre tributos e arrecadação.",
  licitacoes: "Processos licitatórios realizados pelo município.",
  tabelaDiarias: "Valores de diárias para servidores e autoridades municipais.",
  lgpd: "Informações sobre adequação e cumprimento da Lei Geral de Proteção de Dados.",
  // adicione mais conforme necessário
};

export default function Arquivos() {
  const { tipo } = useParams();

  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const title = TITLE_MAP[tipo] || tipo.charAt(0).toUpperCase() + tipo.slice(1).replace(/([A-Z])/g, ' $1');
  const subtitle = SUBTITLE_MAP[tipo] || "Documentos oficiais e publicações relacionadas à gestão municipal.";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      // Validação de segurança
      if (!VALID_TYPES.includes(tipo)) {
        setError("Categoria de documento inválida ou não permitida.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const databaseInstance = getDatabase(firebaseApp);
        const refPath = query(ref(databaseInstance, tipo));

        const snapshot = await get(refPath);
        const data = snapshot.val();

        if (data) {
          const items = Object.keys(data).map((key) => ({
            id: key,
            title: data[key].nome || "Sem título",
            numero: data[key].numero || null,
            mes: data[key].mes || null,
            dia: data[key].dia || null,
            ano: data[key].ano || null,
            href: data[key].href || data[key].documentoUrl || "#",
            timestamp: data[key].timestamp || null,
          }));

          // Ordenação exatamente igual ao Leismunicipais.js
          items.sort((a, b) => {
            const hasTimestampA = !!a.timestamp;
            const hasTimestampB = !!b.timestamp;

            if (hasTimestampA && hasTimestampB) {
              return b.timestamp - a.timestamp;
            }

            if (hasTimestampA && !hasTimestampB) return -1;
            if (!hasTimestampA && hasTimestampB) return 1;

            if (a.ano !== b.ano) return b.ano - a.ano;

            const meses = {
              janeiro: 1, fevereiro: 2, março: 3, abril: 4,
              maio: 5, junho: 6, julho: 7, agosto: 8,
              setembro: 9, outubro: 10, novembro: 11, dezembro: 12
            };

            const mesA = meses[a.mes?.toLowerCase()] || 0;
            const mesB = meses[b.mes?.toLowerCase()] || 0;

            if (mesA !== mesB) return mesB - mesA;

            if (a.dia && b.dia) return Number(b.dia) - Number(a.dia);

            return 0;
          });

          setNewsData(items);
        } else {
          setNewsData([]);
        }
      } catch (error) {
        console.error(`Erro ao carregar ${tipo}:`, error);
        setError("Não foi possível carregar os documentos no momento.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tipo]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3, maxWidth: '1200px', mx: 'auto' }}>
      <TitleSection title={title} subtitle={subtitle} />

      <DataTable data={newsData} />
    </Box>
  );
}